import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../../config/api';
import { PostComposer } from '../../components/PostComposer';
import { getProfileUrl } from '../../utils/profileUtils';
import {
  Video, Eye, Heart, Share2, Users, MessageCircle, BarChart3,
  Upload, Plus, Settings, TrendingUp, DollarSign, Flame, Zap,
  Rocket, ArrowUpRight, ArrowDownRight, Loader2, Film,
  Scissors, Wand2, Radio, Sparkles, Layout
} from 'lucide-react';

function formatCount(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function ContentCreatorDashboard() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [momentum, setMomentum] = useState(null);
  const [creatorSettings, setCreatorSettings] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchDashboardData();
  }, [isAuthenticated, authLoading]);

  const fetchDashboardData = async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, videosRes, momentumRes, settingsRes] = await Promise.all([
        fetch(`${API}/content-creator/my-stats`, { headers }).catch(() => null),
        fetch(`${API}/artist/videos`, { headers }).catch(() => null),
        fetch(`${API}/creator/momentum`, { headers }).catch(() => null),
        fetch(`${API}/content-creator/settings`, { headers }).catch(() => null),
      ]);

      if (statsRes?.ok) setStats(await statsRes.json());
      if (videosRes?.ok) { const d = await videosRes.json(); setVideos(d.videos || []); }
      if (momentumRes?.ok) setMomentum(await momentumRes.json());
      if (settingsRes?.ok) setCreatorSettings(await settingsRes.json());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="content-creator-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center">
              <Video className="w-6 h-6 text-pink-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl font-bold">{user?.name || 'Creator'}</h1>
                <span className="px-2 py-0.5 rounded-full text-xs bg-pink-500/20 text-pink-400">Content Creator</span>
              </div>
              <p className="text-muted-foreground">@{user?.handle || 'handle'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/settings')} data-testid="cc-settings-btn">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button onClick={() => {
              if (!user?.handle) {
                toast.error('Set your @handle first in Settings');
                return;
              }
              navigate(getProfileUrl(user));
            }} data-testid="cc-view-profile-btn">
              View Profile
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="glass-card border-border p-4" data-testid="cc-stat-views">
            <div className="flex items-center gap-3">
              <Eye className="w-8 h-8 text-cyan-400" />
              <div>
                <p className="text-2xl font-bold">{formatCount(stats?.total_views)}</p>
                <p className="text-xs text-muted-foreground">Total Views</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4" data-testid="cc-stat-likes">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 text-red-400" />
              <div>
                <p className="text-2xl font-bold">{formatCount(stats?.total_likes)}</p>
                <p className="text-xs text-muted-foreground">Likes</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4" data-testid="cc-stat-followers">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold">{formatCount(stats?.followers)}</p>
                <p className="text-xs text-muted-foreground">Followers</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4" data-testid="cc-stat-engagement">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-violet-400" />
              <div>
                <p className="text-2xl font-bold">{stats?.engagement_rate || 0}%</p>
                <p className="text-xs text-muted-foreground">Engagement</p>
              </div>
            </div>
          </Card>
          <Card className="glass-card border-border p-4" data-testid="cc-stat-videos">
            <div className="flex items-center gap-3">
              <Film className="w-8 h-8 text-amber-400" />
              <div>
                <p className="text-2xl font-bold">{stats?.video_count || videos.length || 0}</p>
                <p className="text-xs text-muted-foreground">Videos</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="create" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="create" data-testid="cc-create-tab">
              <Plus className="w-4 h-4 mr-2" /> Create
            </TabsTrigger>
            <TabsTrigger value="content" data-testid="cc-content-tab">
              <Film className="w-4 h-4 mr-2" /> My Content
            </TabsTrigger>
            <TabsTrigger value="stats" data-testid="cc-stats-tab">
              <BarChart3 className="w-4 h-4 mr-2" /> Stats
            </TabsTrigger>
            <TabsTrigger value="tools" data-testid="cc-tools-tab">
              <Sparkles className="w-4 h-4 mr-2" /> Tools
            </TabsTrigger>
          </TabsList>

          {/* Create Tab — PostComposer supports video, image, text */}
          <TabsContent value="create">
            <Card className="glass-card border-border p-6">
              <h3 className="font-heading text-lg font-bold mb-1">Create Content</h3>
              <p className="text-xs text-muted-foreground mb-4">Upload a video, photo, or write a post — all in one place.</p>
              <PostComposer onPostCreated={fetchDashboardData} />
            </Card>
          </TabsContent>

          {/* My Content Tab */}
          <TabsContent value="content">
            <ContentList userId={user?.user_id} getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Momentum */}
              <Card className="glass-card border-border p-6" data-testid="cc-momentum">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-violet-400" /> Your Momentum
                </h3>
                {momentum?.message && (
                  <div className="p-3 rounded-xl bg-gradient-to-r from-pink-600/10 to-violet-600/10 border border-pink-500/20 mb-4">
                    <p className="text-sm font-semibold text-foreground">{momentum.message}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">Views Today</span>
                      {momentum?.growth_pct !== 0 && (
                        <span className={`text-[11px] font-bold flex items-center gap-0.5 ${(momentum?.growth_pct || 0) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {(momentum?.growth_pct || 0) > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {Math.abs(momentum?.growth_pct || 0)}%
                        </span>
                      )}
                    </div>
                    <p className="text-2xl font-bold">{(momentum?.today_views || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <span className="text-xs text-muted-foreground">Reactions Today</span>
                    <p className="text-2xl font-bold mt-1">{(momentum?.today_reactions || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <span className="text-xs text-muted-foreground">New Followers</span>
                    <p className="text-2xl font-bold mt-1">+{momentum?.week_followers || 0}</p>
                    <p className="text-[10px] text-muted-foreground">this week</p>
                  </div>
                  <div className="p-3 rounded-xl bg-muted/20 border border-border">
                    <span className="text-xs text-muted-foreground">Trending Posts</span>
                    <p className="text-2xl font-bold mt-1">{momentum?.trending_posts || 0}</p>
                  </div>
                </div>

                {/* Top Content */}
                {stats?.top_content?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-muted-foreground mb-2">Top Performing</h4>
                    <div className="space-y-2">
                      {stats.top_content.slice(0, 3).map((c, i) => (
                        <div key={c.post_id || i} className="flex items-center justify-between p-2 rounded-lg bg-muted/10 border border-border">
                          <span className="text-sm truncate flex-1">{c.content_text?.slice(0, 50) || 'Untitled'}</span>
                          <span className="text-xs text-muted-foreground ml-2">{formatCount(c.views_count)} views</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Quick Actions */}
              <Card className="glass-card border-border p-6" data-testid="cc-growth-actions">
                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-amber-400" /> Grow Your Brand
                </h3>
                <div className="space-y-3">
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/viral-engine')}>
                    <Flame className="w-5 h-5 text-orange-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Viral Engine</p>
                      <p className="text-[11px] text-muted-foreground">AI-powered growth tips</p>
                    </div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/creator/earnings')}>
                    <DollarSign className="w-5 h-5 text-green-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Earnings</p>
                      <p className="text-[11px] text-muted-foreground">Track tips, locks, boosts</p>
                    </div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/live')}>
                    <Radio className="w-5 h-5 text-red-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Go Live</p>
                      <p className="text-[11px] text-muted-foreground">Stream to your audience</p>
                    </div>
                  </Button>
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-border" onClick={() => navigate('/create')}>
                    <Plus className="w-5 h-5 text-cyan-400 mr-3" />
                    <div className="text-left">
                      <p className="font-semibold text-sm">Create Hub</p>
                      <p className="text-[11px] text-muted-foreground">All creation tools in one place</p>
                    </div>
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Creator Tools Tab */}
          <TabsContent value="tools">
            <div className="grid md:grid-cols-3 gap-4">
              <Card className="glass-card border-border p-5 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate('/creator-editor')} data-testid="cc-tool-editor">
                <Scissors className="w-8 h-8 text-pink-400 mb-3" />
                <h4 className="font-heading font-bold mb-1">Creator Editor</h4>
                <p className="text-xs text-muted-foreground">Edit photos & videos with overlays</p>
              </Card>
              <Card className="glass-card border-border p-5 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate('/viral-engine')} data-testid="cc-tool-viral">
                <Flame className="w-8 h-8 text-orange-400 mb-3" />
                <h4 className="font-heading font-bold mb-1">Viral Engine</h4>
                <p className="text-xs text-muted-foreground">Trending analytics & scoring</p>
              </Card>
              <Card className="glass-card border-border p-5 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate('/content-locks')} data-testid="cc-tool-locks">
                <Layout className="w-8 h-8 text-emerald-400 mb-3" />
                <h4 className="font-heading font-bold mb-1">Content Locks</h4>
                <p className="text-xs text-muted-foreground">Gate content for engagement or pay</p>
              </Card>
              <Card className="glass-card border-border p-5 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => navigate('/creator-subscriptions')} data-testid="cc-tool-subs">
                <DollarSign className="w-8 h-8 text-green-400 mb-3" />
                <h4 className="font-heading font-bold mb-1">Subscriptions</h4>
                <p className="text-xs text-muted-foreground">Manage fan subscription tiers</p>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function ContentList({ userId, getAuthHeaders }) {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchContent();
  }, [userId, filter]);

  const fetchContent = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const contentType = filter === 'all' ? 'all' : filter;
      const res = await fetch(
        `${API}/content-creator/content/${userId}?content_type=${contentType}&sort_by=recent&limit=30`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setContent(data.content || []);
      }
    } catch (err) {
      console.error('Failed to load content:', err);
    } finally {
      setLoading(false);
    }
  };

  const filters = [
    { value: 'all', label: 'All' },
    { value: 'video', label: 'Videos' },
    { value: 'image', label: 'Photos' },
    { value: 'text', label: 'Posts' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="cc-content-list">
      {/* Filter pills */}
      <div className="flex gap-2 mb-4">
        {filters.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.value
                ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                : 'bg-muted/20 text-muted-foreground border border-border hover:bg-muted/40'
            }`}
            data-testid={`cc-filter-${f.value}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {content.length === 0 ? (
        <Card className="glass-card border-border p-12 text-center">
          <Film className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground font-medium">No content yet</p>
          <p className="text-xs text-muted-foreground mt-1">Create your first post to get started!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {content.map((item, idx) => (
            <Card key={`${item.post_id}-${idx}`} className="glass-card border-border overflow-hidden group" data-testid={`cc-content-${item.post_id}`}>
              {item.media_url && (item.media_type === 'video' || item.media_type === 'reel') ? (
                <div className="aspect-[4/5] bg-muted/20 relative">
                  <video src={item.media_url.startsWith('http') ? item.media_url : `${API}/uploads/posts/${item.media_url}`}
                    className="w-full h-full object-cover" muted preload="metadata" />
                  <div className="absolute top-2 left-2">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white">VIDEO</span>
                  </div>
                </div>
              ) : item.media_url && item.media_type === 'image' ? (
                <div className="aspect-square bg-muted/20">
                  <img src={item.media_url.startsWith('http') ? item.media_url : `${API}/uploads/posts/${item.media_url}`}
                    className="w-full h-full object-cover" alt="" loading="lazy" />
                </div>
              ) : (
                <div className="aspect-square bg-muted/10 flex items-center justify-center p-3">
                  <p className="text-xs text-muted-foreground line-clamp-6">{item.content_text || 'Text post'}</p>
                </div>
              )}
              <div className="p-2">
                <p className="text-xs truncate">{item.content_text?.slice(0, 60) || 'Untitled'}</p>
                <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{item.views_count || 0}</span>
                  <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{item.likes_count || 0}</span>
                  <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{item.comments_count || 0}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
